import React, { useState } from 'react';
import { useStore } from '../../stores/useStore';
import { Feedback, Condition } from '../../types';

interface FeedbackFormProps {
    onComplete?: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ onComplete }) => {
    const { selectedScheduleId, schedules, machines, clients, addFeedback, setActiveTab } = useStore();

    const schedule = schedules.find((s) => s.id === selectedScheduleId);
    const machine = schedule?.machineId ? machines.find((m) => m.id === schedule.machineId) : undefined;
    const client = schedule ? clients.find((c) => c.id === schedule.clientId) : undefined;

    const [photos, setPhotos] = useState<string[]>([]);
    const [condition, setCondition] = useState<Condition>('양호');
    const [workDesc, setWorkDesc] = useState('');
    const [issues, setIssues] = useState('');
    const [resolution, setResolution] = useState('');
    const [followUp, setFollowUp] = useState(false);

    const handleTakePhoto = () => {
        if (photos.length < 4) {
            setPhotos([...photos, `photo_${Date.now()}`]);
        }
    };

    const removePhoto = (index: number) => {
        setPhotos(photos.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        if (!schedule) return;

        const feedback: Feedback = {
            id: `fb_${Date.now()}`,
            scheduleId: schedule.id,
            machineId: schedule.machineId || '',
            workDetails: {
                type: schedule.type,
                description: workDesc,
                issuesFound: issues,
                resolution,
            },
            sitePhotos: photos,
            condition,
            partsUsed: [],
            customerSignature: '',
            technicianSignature: '',
            laborCost: 0,
            partsCost: 0,
            totalCost: 0,
            paymentMethod: '미수',
            followUpNeeded: followUp,
            submittedAt: new Date().toISOString(),
        };

        addFeedback(feedback);
        alert('피드백이 제출되었습니다!');
        if (onComplete) {
            onComplete();
        } else {
            setActiveTab('work');
        }
    };

    if (!schedule) {
        return (
            <div style={{ padding: '32px', textAlign: 'center', color: '#a09890' }}>
                <p>왼쪽 일정 탭에서 작업을 선택해주세요</p>
                <button
                    onClick={() => setActiveTab('schedule')}
                    style={{ marginTop: '16px', color: '#1e3a5f', fontWeight: 'bold', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                    일정으로 이동 →
                </button>
            </div>
        );
    }

    const sectionStyle: React.CSSProperties = { marginBottom: '20px' };
    const labelStyle: React.CSSProperties = {
        fontSize: '12px', fontWeight: 700, marginBottom: '6px',
        display: 'block', color: '#5a5a5a', letterSpacing: '0.3px',
    };
    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '10px 12px',
        border: '1px solid #c4b8a5', borderRadius: '10px',
        fontSize: '14px', fontFamily: "'Noto Sans KR', sans-serif",
        outline: 'none', backgroundColor: 'white', boxSizing: 'border-box', color: '#2c2c2c',
    };

    return (
        <div style={{ padding: '16px', paddingBottom: '32px' }}>
            {/* 작업 정보 */}
            <div style={{ backgroundColor: '#f4f1ea', border: '1px solid #c4b8a5', borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
                <p style={{ fontWeight: 700, color: '#1e3a5f', fontSize: '15px', marginBottom: '6px' }}>{schedule.memo}</p>
                <p style={{ fontSize: '13px', color: '#5a5a5a' }}>{schedule.date} · {schedule.location}</p>
                {client && <p style={{ fontSize: '13px', color: '#5a5a5a' }}>{client.name}</p>}
                {machine && <p style={{ fontSize: '13px', color: '#5a5a5a' }}>{machine.name} ({machine.model})</p>}
            </div>

            {/* 현장 사진 */}
            <div style={sectionStyle}>
                <label style={labelStyle}>현장 사진 ({photos.length}/4)</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {photos.map((photo, i) => (
                        <div key={photo} style={{ position: 'relative', aspectRatio: '1', backgroundColor: '#e8e0d5', border: '1px solid #c4b8a5' }}>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#a09890' }}>
                                사진 {i + 1}
                            </div>
                            <button
                                onClick={() => removePhoto(i)}
                                style={{
                                    position: 'absolute', top: '4px', right: '4px',
                                    width: '18px', height: '18px',
                                    backgroundColor: '#c75c2e', color: 'white',
                                    borderRadius: '10px', border: 'none', fontSize: '10px',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    {photos.length < 4 && (
                        <button
                            onClick={handleTakePhoto}
                            style={{
                                aspectRatio: '1', backgroundColor: '#f4f1ea',
                                border: '1px dashed #c4b8a5', display: 'flex',
                                flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', gap: '4px', cursor: 'pointer', color: '#a09890',
                            }}
                        >
                            <span style={{ fontSize: '18px' }}>📷</span>
                            <span style={{ fontSize: '11px' }}>촬영</span>
                        </button>
                    )}
                </div>
            </div>

            {/* 작업 내용 */}
            <div style={sectionStyle}>
                <label style={labelStyle}>작업 내용</label>
                <textarea
                    value={workDesc}
                    onChange={(e) => setWorkDesc(e.target.value)}
                    placeholder="수행한 작업을 상세히 기록하세요"
                    style={{ ...inputStyle, height: '88px', resize: 'none' }}
                />
            </div>

            {/* 발견 문제점 */}
            <div style={sectionStyle}>
                <label style={labelStyle}>발견 문제점</label>
                <textarea
                    value={issues}
                    onChange={(e) => setIssues(e.target.value)}
                    placeholder="발견된 문제점이나 특이사항"
                    style={{ ...inputStyle, height: '72px', resize: 'none' }}
                />
            </div>

            {/* 해결/처리 내용 */}
            <div style={sectionStyle}>
                <label style={labelStyle}>해결 / 처리 내용</label>
                <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder="적용한 해결책 또는 처리 내용"
                    style={{ ...inputStyle, height: '72px', resize: 'none' }}
                />
            </div>

            {/* 기기 상태 */}
            <div style={sectionStyle}>
                <label style={labelStyle}>기기 상태 판정</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    {(['양호', '주의', '수리필요', '교체권장'] as Condition[]).map((c) => (
                        <button
                            key={c}
                            onClick={() => setCondition(c)}
                            style={{
                                padding: '11px',
                                border: condition === c ? '2px solid #1e3a5f' : '1px solid #c4b8a5',
                                backgroundColor: condition === c ? '#1e3a5f' : 'white',
                                color: condition === c ? 'white' : '#5a5a5a',
                                fontSize: '13px', fontWeight: 700,
                                cursor: 'pointer', borderRadius: '10px',
                                fontFamily: "'Noto Sans KR', sans-serif",
                            }}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            {/* 후속 작업 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                <input
                    type="checkbox"
                    id="followup"
                    checked={followUp}
                    onChange={(e) => setFollowUp(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="followup" style={{ fontSize: '14px', color: '#2c2c2c', cursor: 'pointer' }}>후속 작업 필요</label>
            </div>

            <button
                onClick={handleSubmit}
                style={{
                    width: '100%', backgroundColor: '#c75c2e', color: 'white',
                    padding: '14px', borderRadius: '10px', fontWeight: 700,
                    fontSize: '15px', border: 'none', cursor: 'pointer',
                    fontFamily: "'Noto Sans KR', sans-serif",
                }}
            >
                피드백 제출
            </button>
        </div>
    );
};
